import { useEffect, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { CountrySelect } from '../components/CountrySelect.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, TableWrap, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, ApiError } from '../lib/api.ts';
import { dateTime, since } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { ComposeDialog, EmailsTab, MessageDialog, SendingSettings, TemplatesTab } from './OutreachMail.tsx';
import { DemoForm, DemosTab, RecordReplyDialog, RepliesTab, ResultsTab } from './OutreachReplies.tsx';
import { EnrollDialog, enrollmentText, SequencesTab, type CompanySequence } from './OutreachSequences.tsx';
import { Kpi, Table } from './Reports.tsx';

/**
 * Sales outreach: the companies the business wants to win, the people to write to at each one,
 * and what happened with them. A company is one card: its details, its people (each with the
 * reason the business may write to them), its stage and its timeline.
 */

export type Stage = 'new' | 'contacted' | 'replied' | 'interested' | 'demo' | 'customer' | 'not_interested' | 'do_not_contact';
export const STAGES: Stage[] = ['new', 'contacted', 'replied', 'interested', 'demo', 'customer', 'not_interested', 'do_not_contact'];
type Basis = 'consent' | 'legitimate_interest' | 'customer';
const BASES: Basis[] = ['legitimate_interest', 'consent', 'customer'];
type Blocked = 'archived' | 'no_email' | 'no_basis' | 'bounced' | 'unsubscribed' | 'company_closed';
type Tab = 'companies' | 'contacts' | 'replies' | 'demos' | 'emails' | 'sequences' | 'templates' | 'results';
const TABS: { key: Tab; icon: IconName }[] = [
  { key: 'companies', icon: 'store' },
  { key: 'contacts', icon: 'users' },
  { key: 'replies', icon: 'reply' },
  { key: 'demos', icon: 'calendar' },
  { key: 'emails', icon: 'send' },
  { key: 'sequences', icon: 'repeat' },
  { key: 'templates', icon: 'file' },
  { key: 'results', icon: 'chart' },
];

interface Overview {
  companies: number;
  stages: Record<Stage, number>;
  contacts: number;
  reachable: number;
  no_basis: number;
  no_email: number;
  industries: { industry: string; n: number }[];
  replies_new: number;
}
interface Setup {
  industries: string[];
  tags: string[];
  countries: string[];
  people: { id: string; name: string }[];
}
interface CompanyRow {
  id: string;
  name: string;
  domain: string | null;
  industry: string | null;
  country: string | null;
  city: string | null;
  fleet_size: number | null;
  employees: number | null;
  tags: string[];
  stage: Stage;
  owner: string | null;
  owner_name: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_title: string | null;
  contacts: number;
  reachable: number;
  last_activity_at: string | null;
  created_at: string;
  active: boolean;
}
interface ContactFull {
  id: string;
  company: string;
  first_name: string;
  last_name: string | null;
  job_title: string | null;
  email: string | null;
  phone: string | null;
  linkedin: string | null;
  is_primary: boolean;
  basis: Basis | null;
  basis_note: string | null;
  basis_at: string | null;
  basis_by_name: string | null;
  email_state: 'unknown' | 'bounced';
  bounced_at: string | null;
  bounce_reason: string | null;
  opted_out_at: string | null;
  blocked: Blocked | null;
  active: boolean;
}
interface TimelineEvent {
  id: string;
  kind: string;
  text: string | null;
  data: Record<string, unknown>;
  at: string;
  actor_name: string | null;
  contact_name: string | null;
}
interface Company extends Omit<CompanyRow, 'contacts' | 'reachable' | 'contact_name' | 'contact_email' | 'contact_title'> {
  website: string | null;
  phone: string | null;
  email: string | null;
  source: string;
  note: string | null;
  lead: string | null;
  lead_title: string | null;
  contacts: ContactFull[];
  events: TimelineEvent[];
}
interface ContactRow {
  id: string;
  company: string;
  company_name: string;
  company_stage: Stage;
  country: string | null;
  first_name: string;
  last_name: string | null;
  job_title: string | null;
  email: string | null;
  is_primary: boolean;
  basis: Basis | null;
  blocked: Blocked | null;
}

const STAGE_LOOK: Record<Stage, { tone: Tone; icon: IconName }> = {
  new: { tone: 'neutral', icon: 'circle' },
  contacted: { tone: 'info', icon: 'send' },
  replied: { tone: 'primary', icon: 'reply' },
  interested: { tone: 'ok', icon: 'star' },
  demo: { tone: 'ok', icon: 'calendar' },
  customer: { tone: 'ok', icon: 'badgeCheck' },
  not_interested: { tone: 'warn', icon: 'x' },
  do_not_contact: { tone: 'danger', icon: 'bellOff' },
};
const BLOCK_LOOK: Record<Blocked, { tone: Tone; icon: IconName }> = {
  archived: { tone: 'neutral', icon: 'archive' },
  no_email: { tone: 'neutral', icon: 'mail' },
  no_basis: { tone: 'warn', icon: 'shield' },
  bounced: { tone: 'danger', icon: 'alert' },
  unsubscribed: { tone: 'warn', icon: 'bellOff' },
  company_closed: { tone: 'neutral', icon: 'x' },
};
/** EU and EEA countries, where rules for business e-mail differ by country: the form reminds people to check. */
const EU = new Set(['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS', 'LI', 'NO']);
const INDUSTRY_KEYS = ['transport', 'logistics', 'distribution', 'security', 'fleet', 'construction', 'public', 'retail', 'manufacturing', 'services'] as const;

export function StageBadge({ stage }: { stage: Stage }) {
  return <StatusBadge tone={STAGE_LOOK[stage].tone} icon={STAGE_LOOK[stage].icon} label={t(`out.st_${stage}` as MessageKey)} />;
}
function ReachBadge({ blocked }: { blocked: Blocked | null }) {
  if (!blocked) return <StatusBadge tone="ok" icon="checkCircle" label={t('out.can_write')} />;
  return <StatusBadge tone={BLOCK_LOOK[blocked].tone} icon={BLOCK_LOOK[blocked].icon} label={t(`out.bl_${blocked}` as MessageKey)} />;
}
const personName = (p: { first_name: string; last_name: string | null }) => [p.first_name, p.last_name].filter(Boolean).join(' ');
const countryName = (code: string | null) => {
  if (!code) return null;
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
};
function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="out-toggle">
      <span>{label}</span>
      <Switch label={label} checked={checked} onChange={onChange} />
    </label>
  );
}
const place = (c: { city: string | null; country: string | null }) => [c.city, countryName(c.country)].filter(Boolean).join(', ');

export function Outreach() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const tab = (TABS.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'companies') as Tab;
  const { data: o, reload } = useAction<Overview>('outreach.overview');
  const [version, setVersion] = useState(0);
  const [adding, setAdding] = useState(false);
  const [settings, setSettings] = useState(false);
  const open = params.get('co');
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) v === null ? p.delete(k) : p.set(k, v);
    setParams(p, { replace: true });
  };
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  const manage = can('outreach.manage');
  const talking = o ? o.stages.replied + o.stages.interested : 0;
  return (
    <Shell>
      <div className="page reports out-page">
        <div className="page-head">
          <div>
            <h1>{t('out.title')}</h1>
            <p className="muted">{t('out.subtitle')}</p>
          </div>
          {manage || can('outreach.send') ? (
            <div className="page-actions">
              {can('outreach.send') ? (
                <Button variant="secondary" icon="settings" onClick={() => setSettings(true)}>
                  {t('om.settings_button')}
                </Button>
              ) : null}
              {manage && can('data.import') ? (
                <Link className="btn btn-secondary" to="/manage/import?target=prospects">
                  <Icon name="upload" size={18} />
                  {t('out.import')}
                </Link>
              ) : null}
              {manage ? (
                <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
                  {t('out.add_company')}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <section className="rep-kpis" aria-label={t('out.at_a_glance')}>
            <Kpi label={t('out.k_companies')} value={String(o.companies)} hint={tn('out.k_new', o.stages.new)} />
            <Kpi
              label={t('out.k_reachable')}
              value={String(o.reachable)}
              hint={o.no_basis ? tn('out.k_no_basis', o.no_basis) : tn('out.k_people', o.contacts)}
              warn={o.no_basis > 0}
            />
            <Kpi label={t('out.k_talking')} value={String(talking)} hint={tn('out.k_contacted', o.stages.contacted)} />
            <Kpi label={t('out.k_demos')} value={String(o.stages.demo)} hint={tn('out.k_customers', o.stages.customer)} />
          </section>
        )}
        <div className="segmented tabs out-tabs" role="tablist" aria-label={t('out.sections')}>
          {TABS.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === 'companies' ? null : x.key })}>
              <Icon name={x.icon} size={16} />
              {t(`out.tab_${x.key}` as MessageKey)}
              {x.key === 'replies' && o?.replies_new ? (
                <span className="orp-count" aria-label={tn('orp.new_n', o.replies_new)}>
                  {o.replies_new}
                </span>
              ) : null}
            </button>
          ))}
        </div>
        {tab === 'companies' ? (
          <CompaniesTab key={version} params={params} set={set} onAdd={manage ? () => setAdding(true) : undefined} onChange={changed} />
        ) : tab === 'contacts' ? (
          <ContactsTab key={version} onOpen={(id) => set({ co: id })} />
        ) : tab === 'replies' ? (
          <RepliesTab key={version} onOpenCompany={(id) => set({ co: id })} />
        ) : tab === 'demos' ? (
          <DemosTab key={version} onOpenCompany={(id) => set({ co: id })} />
        ) : tab === 'results' ? (
          <ResultsTab key={version} />
        ) : tab === 'emails' ? (
          <EmailsTab key={version} onOpenCompany={(id) => set({ co: id })} />
        ) : tab === 'sequences' ? (
          <SequencesTab key={version} />
        ) : (
          <TemplatesTab />
        )}
      </div>
      {adding ? (
        <CompanyForm
          company={null}
          onClose={() => setAdding(false)}
          onOpenOther={(id) => (setAdding(false), set({ co: id }))}
          onSaved={async (id) => {
            setAdding(false);
            await changed();
            set({ co: id });
          }}
        />
      ) : null}
      {open ? <CompanyDialog key={open} id={open} onClose={() => set({ co: null })} onChanged={changed} /> : null}
      {settings ? <SendingSettings onClose={() => setSettings(false)} onSaved={changed} /> : null}
    </Shell>
  );
}

// ---- companies ---------------------------------------------------------------------------------

const STAGE_FILTERS = ['open', 'all', ...STAGES] as const;
type StageFilter = (typeof STAGE_FILTERS)[number];

function CompaniesTab({ params, set, onAdd, onChange }: { params: URLSearchParams; set: (n: Record<string, string | null>) => void; onAdd?: () => void; onChange: () => Promise<void> }) {
  const { can } = useSession();
  const manage = can('outreach.manage');
  const { data: setup } = useAction<Setup>('outreach.setup');
  const stage = (STAGE_FILTERS as readonly string[]).includes(params.get('stage') ?? '') ? (params.get('stage') as StageFilter) : 'open';
  const industry = params.get('industry') ?? '';
  const country = params.get('country') ?? '';
  const tag = params.get('tag') ?? '';
  const owner = params.get('owner') ?? '';
  const archived = params.get('archived') === '1';
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState<'tag' | 'owner' | 'stage' | null>(null);
  const [writing, setWriting] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => (setQ(search.trim()), setOffset(0)), 300);
    return () => clearTimeout(id);
  }, [search]);
  const input = {
    stage,
    archived,
    offset,
    ...(industry ? { industry } : {}),
    ...(country ? { country } : {}),
    ...(tag ? { tag } : {}),
    ...(owner ? { owner } : {}),
    ...(q ? { search: q } : {}),
  };
  const { data, reload } = useAction<{ total: number; rows: CompanyRow[] }>('outreach.companies', input);
  const filter = (k: string, v: string) => {
    set({ [k]: v || null });
    setOffset(0);
    setPicked(new Set());
  };
  const filtered = Boolean(industry || country || tag || owner || q || archived || stage !== 'open');
  const toggle = (id: string) =>
    setPicked((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const all = data?.rows ?? [];
  const allPicked = all.length > 0 && all.every((r) => picked.has(r.id));
  return (
    <section className="panel">
      <div className="out-filters">
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={search} placeholder={t('out.search')} aria-label={t('out.search')} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <select className="input" aria-label={t('out.f_stage')} value={stage} onChange={(e) => filter('stage', e.target.value === 'open' ? '' : e.target.value)}>
          {STAGE_FILTERS.map((s) => (
            <option key={s} value={s}>
              {s === 'open' ? t('out.f_open') : s === 'all' ? t('out.f_all_stages') : t(`out.st_${s}` as MessageKey)}
            </option>
          ))}
        </select>
        <select className="input" aria-label={t('out.f_industry')} value={industry} onChange={(e) => filter('industry', e.target.value)}>
          <option value="">{t('out.f_any_industry')}</option>
          {(setup?.industries ?? []).map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </select>
        <select className="input" aria-label={t('out.f_country')} value={country} onChange={(e) => filter('country', e.target.value)}>
          <option value="">{t('out.f_any_country')}</option>
          {(setup?.countries ?? []).map((x) => (
            <option key={x} value={x}>
              {countryName(x)}
            </option>
          ))}
        </select>
        {setup?.tags.length ? (
          <select className="input" aria-label={t('out.f_tag')} value={tag} onChange={(e) => filter('tag', e.target.value)}>
            <option value="">{t('out.f_any_tag')}</option>
            {setup.tags.map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </select>
        ) : null}
        <select className="input" aria-label={t('out.f_owner')} value={owner} onChange={(e) => filter('owner', e.target.value)}>
          <option value="">{t('out.f_anyone')}</option>
          <option value="me">{t('out.f_mine')}</option>
          <option value="none">{t('out.f_no_owner')}</option>
          {(setup?.people ?? []).map((x) => (
            <option key={x.id} value={x.id}>
              {x.name}
            </option>
          ))}
        </select>
        <Toggle label={t('out.f_archived')} checked={archived} onChange={(v) => filter('archived', v ? '1' : '')} />
      </div>
      {manage && picked.size ? (
        <div className="out-bulk" role="region" aria-label={t('out.bulk_label')}>
          <span className="strong">{tn('out.n_picked', picked.size)}</span>
          <span className="row-actions">
            {can('outreach.send') ? (
              <Button size="sm" variant="primary" icon="send" onClick={() => setWriting(true)}>
                {t('om.bulk_write')}
              </Button>
            ) : null}
            {can('outreach.send') ? (
              <Button size="sm" variant="secondary" icon="repeat" onClick={() => setEnrolling(true)}>
                {t('sq.bulk_add')}
              </Button>
            ) : null}
            <Button size="sm" variant="secondary" icon="bookmark" onClick={() => setBulk('tag')}>
              {t('out.bulk_tag')}
            </Button>
            <Button size="sm" variant="secondary" icon="users" onClick={() => setBulk('owner')}>
              {t('out.bulk_owner')}
            </Button>
            <Button size="sm" variant="secondary" icon="layers" onClick={() => setBulk('stage')}>
              {t('out.bulk_stage')}
            </Button>
            <Button size="sm" variant="ghost" icon="x" onClick={() => setPicked(new Set())}>
              {t('out.bulk_clear')}
            </Button>
          </span>
        </div>
      ) : null}
      {!data ? (
        <Skeleton h={280} />
      ) : !data.rows.length && !filtered ? (
        <EmptyState
          icon="store"
          title={t('out.empty')}
          body={t('out.empty_hint')}
          action={
            onAdd ? (
              <Button variant="primary" icon="plus" onClick={onAdd}>
                {t('out.add_company')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <TableWrap label={t('out.tab_companies')}>
            <Table
              view="outreach.companies"
              head={[
                ...(manage ? [''] : []),
                t('out.c_company'),
                t('out.c_contact'),
                t('out.c_industry'),
                t('out.c_fleet'),
                t('out.c_stage'),
                t('out.c_owner'),
                t('out.c_activity'),
              ]}
              phoneHide={manage ? [0, 2, 3, 4, 6, 7] : [1, 2, 3, 5, 6]}
              left={manage ? [1, 2, 3, 5, 6] : [1, 2, 4, 5]}
              empty={t('out.none_match')}
              rows={data.rows.map((r) => ({
                key: r.id,
                onClick: () => set({ co: r.id }),
                sort: [
                  ...(manage ? [null] : []),
                  r.name.toLowerCase(),
                  r.contact_name?.toLowerCase() ?? null,
                  r.industry?.toLowerCase() ?? null,
                  r.fleet_size,
                  STAGES.indexOf(r.stage),
                  r.owner_name?.toLowerCase() ?? null,
                  r.last_activity_at ?? r.created_at,
                ],
                cells: [
                  ...(manage
                    ? [
                        <input
                          key="pick"
                          type="checkbox"
                          className="out-pick"
                          checked={picked.has(r.id)}
                          aria-label={t('out.pick', { name: r.name })}
                          onChange={() => toggle(r.id)}
                          onClick={(e) => e.stopPropagation()}
                        />,
                      ]
                    : []),
                  <span key="n" className="vat-party">
                    <span className="strong">{r.name}</span>
                    <span className="muted small">{[r.domain, place(r)].filter(Boolean).join(' · ') || '—'}</span>
                  </span>,
                  r.contact_name ? (
                    <span key="c" className="vat-party">
                      <span>{r.contact_name}</span>
                      <span className="muted small">{t('out.n_of_reach', { r: r.reachable, n: r.contacts })}</span>
                    </span>
                  ) : (
                    <span key="c" className="muted">
                      {t('out.no_contact')}
                    </span>
                  ),
                  r.industry ?? '—',
                  r.fleet_size === null ? '—' : String(r.fleet_size),
                  <StageBadge key="s" stage={r.stage} />,
                  r.owner_name ?? '—',
                  <span key="a" className="muted small">
                    {since(r.last_activity_at ?? r.created_at)}
                  </span>,
                ],
              }))}
            />
          </TableWrap>
          <div className="row-actions mkt-pager">
            {manage && all.length ? (
              <Button size="sm" variant="ghost" icon={allPicked ? 'x' : 'checkSquare'} onClick={() => setPicked(allPicked ? new Set() : new Set(all.map((r) => r.id)))}>
                {allPicked ? t('out.unpick_all') : t('out.pick_all')}
              </Button>
            ) : null}
            <span className="muted small">{t('out.showing', { from: data.total ? offset + 1 : 0, to: offset + data.rows.length, total: data.total })}</span>
            <IconButton icon="back" label={t('out.prev')} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 100))} />
            <IconButton icon="chevronRight" label={t('out.next')} disabled={offset + data.rows.length >= data.total} onClick={() => setOffset(offset + 100)} />
          </div>
        </>
      )}
      {enrolling ? (
        <EnrollDialog
          companies={[...picked]}
          onClose={() => setEnrolling(false)}
          onDone={async () => {
            setPicked(new Set());
            await reload();
            await onChange();
          }}
        />
      ) : null}
      {writing ? (
        <ComposeDialog
          companies={[...picked]}
          onClose={() => setWriting(false)}
          onSent={async () => {
            setPicked(new Set());
            await reload();
            await onChange();
          }}
        />
      ) : null}
      {bulk ? (
        <BulkDialog
          op={bulk}
          ids={[...picked]}
          setup={setup ?? null}
          onClose={() => setBulk(null)}
          onDone={async () => {
            setBulk(null);
            setPicked(new Set());
            await reload();
            await onChange();
          }}
        />
      ) : null}
    </section>
  );
}

function BulkDialog({ op, ids, setup, onClose, onDone }: { op: 'tag' | 'owner' | 'stage'; ids: string[]; setup: Setup | null; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [tag, setTag] = useState('');
  const [remove, setRemove] = useState(false);
  const [owner, setOwner] = useState('');
  const [stage, setStage] = useState<Stage>('contacted');
  const ok = op !== 'tag' || tag.trim().length > 0;
  const go = () =>
    void run(async () => {
      const body =
        op === 'tag' ? { op: remove ? 'untag' : 'tag', ids, tag: tag.trim() } : op === 'owner' ? { op, ids, owner: owner || null } : { op, ids, stage };
      const r = await action<{ changed: number }>('outreach.bulk', body);
      await onDone();
      return r;
    }, t('out.bulk_done'));
  return (
    <Dialog
      title={t(`out.bulk_${op}_title` as MessageKey, { n: ids.length })}
      onClose={onClose}
      size="sm"
      footer={
        <Button variant="primary" icon="check" busy={busy} disabled={!ok} onClick={go}>
          {t('out.bulk_apply')}
        </Button>
      }
    >
      <div className="form-stack">
        {op === 'tag' ? (
          <>
            <Field label={t('out.tag')} required>
              <input className="input" list="out-tags" value={tag} maxLength={40} onChange={(e) => setTag(e.target.value)} />
            </Field>
            <datalist id="out-tags">
              {(setup?.tags ?? []).map((x) => (
                <option key={x} value={x} />
              ))}
            </datalist>
            <Toggle label={t('out.bulk_untag')} checked={remove} onChange={setRemove} />
          </>
        ) : op === 'owner' ? (
          <Field label={t('out.owner')}>
            <select className="input" value={owner} onChange={(e) => setOwner(e.target.value)}>
              <option value="">{t('out.f_no_owner')}</option>
              {(setup?.people ?? []).map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <Field label={t('out.c_stage')}>
            <select className="input" value={stage} onChange={(e) => setStage(e.target.value as Stage)}>
              {STAGES.map((s) => (
                <option key={s} value={s}>
                  {t(`out.st_${s}` as MessageKey)}
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>
    </Dialog>
  );
}

// ---- adding and changing a company -------------------------------------------------------------

function numberOrNull(v: string) {
  const d = v.replace(/[^\d]/g, '');
  return d ? Math.min(10_000_000, Number(d)) : null;
}

function CompanyForm({
  company,
  onClose,
  onSaved,
  onOpenOther,
}: {
  company: Company | null;
  onClose: () => void;
  onSaved: (id: string) => Promise<void>;
  onOpenOther?: (id: string) => void;
}) {
  const { data: setup } = useAction<Setup>('outreach.setup');
  const { busy, run } = useSave();
  const [f, setF] = useState({
    name: company?.name ?? '',
    website: company?.website ?? '',
    industry: company?.industry ?? '',
    country: company?.country ?? '',
    city: company?.city ?? '',
    phone: company?.phone ?? '',
    email: company?.email ?? '',
    fleet_size: company?.fleet_size === null || company?.fleet_size === undefined ? '' : String(company.fleet_size),
    employees: company?.employees === null || company?.employees === undefined ? '' : String(company.employees),
    owner: company?.owner ?? '',
    note: company?.note ?? '',
  });
  const [tags, setTags] = useState<string[]>(company?.tags ?? []);
  const [tagText, setTagText] = useState('');
  const [tried, setTried] = useState(false);
  const [dup, setDup] = useState<{ id: string; message: string } | null>(null);
  const up = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }));
  const errors = {
    name: !f.name.trim() ? t('out.e_name') : null,
    website: f.website.trim() && !/^(https?:\/\/)?[a-z0-9-]+(\.[a-z0-9-]+)+(\/.*)?$/i.test(f.website.trim()) ? t('out.e_website') : null,
    email: f.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim()) ? t('out.e_email') : null,
  };
  const addTag = () => {
    const v = tagText.trim();
    if (v && !tags.some((x) => x.toLowerCase() === v.toLowerCase()) && tags.length < 12) setTags([...tags, v]);
    setTagText('');
  };
  const industries = Array.from(new Set([...(setup?.industries ?? []), ...INDUSTRY_KEYS.map((k) => t(`out.ind_${k}` as MessageKey))]));
  const save = () => {
    setTried(true);
    if (errors.name || errors.website || errors.email) return;
    setDup(null);
    void run(
      async () => {
        try {
          const out = await action<{ id: string }>('outreach.save_company', {
            id: company?.id ?? null,
            name: f.name.trim(),
            website: f.website.trim() || null,
            industry: f.industry.trim() || null,
            country: f.country || null,
            city: f.city.trim() || null,
            phone: f.phone.trim() || null,
            email: f.email.trim() || null,
            fleet_size: numberOrNull(f.fleet_size),
            employees: numberOrNull(f.employees),
            tags: tagText.trim() ? [...tags, tagText.trim()] : tags,
            owner: f.owner || null,
            note: f.note.trim() || null,
          });
          await onSaved(out.id);
        } catch (err) {
          if (err instanceof ApiError && err.code === 'conflict' && typeof err.details?.id === 'string') {
            setDup({ id: err.details.id, message: err.message });
            return;
          }
          throw err;
        }
      },
      company ? t('out.saved') : undefined,
    );
  };
  return (
    <Dialog
      title={company ? t('out.edit_company', { name: company.name }) : t('out.add_company')}
      onClose={onClose}
      size="lg"
      footer={
        <Button variant="primary" icon="check" busy={busy} onClick={save}>
          {company ? t('common.save') : t('out.add')}
        </Button>
      }
    >
      <div className="form-stack">
        {dup ? (
          <div className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>{dup.message}</span>
            {onOpenOther ? (
              <Button size="sm" variant="secondary" onClick={() => onOpenOther(dup.id)}>
                {t('out.open_it')}
              </Button>
            ) : null}
          </div>
        ) : null}
        <div className="form-grid">
          <Field label={t('out.name')} required error={tried ? errors.name : null}>
            <input className="input" value={f.name} maxLength={160} placeholder={t('out.name_example')} onChange={(e) => up('name')(e.target.value)} data-autofocus />
          </Field>
          <Field label={t('out.website')} error={tried ? errors.website : null} hint={t('out.website_hint')}>
            <input className="input" value={f.website} maxLength={200} inputMode="url" placeholder="example.com" onChange={(e) => up('website')(e.target.value)} />
          </Field>
          <Field label={t('out.industry')} hint={t('out.industry_hint')}>
            <input className="input" list="out-industries" value={f.industry} maxLength={60} onChange={(e) => up('industry')(e.target.value)} />
          </Field>
          <Field label={t('out.owner')}>
            <select className="input" value={f.owner} onChange={(e) => up('owner')(e.target.value)}>
              <option value="">{t('out.f_no_owner')}</option>
              {(setup?.people ?? []).map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('out.country')}>
            <CountrySelect value={f.country} onChange={up('country')} />
          </Field>
          <Field label={t('out.city')}>
            <input className="input" value={f.city} maxLength={80} onChange={(e) => up('city')(e.target.value)} />
          </Field>
          <Field label={t('out.fleet')} hint={t('out.fleet_hint')}>
            <input className="input tabular" inputMode="numeric" value={f.fleet_size} onChange={(e) => up('fleet_size')(e.target.value.replace(/[^\d]/g, ''))} />
          </Field>
          <Field label={t('out.employees')}>
            <input className="input tabular" inputMode="numeric" value={f.employees} onChange={(e) => up('employees')(e.target.value.replace(/[^\d]/g, ''))} />
          </Field>
          <Field label={t('out.phone')}>
            <input className="input" type="tel" value={f.phone} maxLength={40} onChange={(e) => up('phone')(e.target.value)} />
          </Field>
          <Field label={t('out.company_email')} error={tried ? errors.email : null} hint={t('out.company_email_hint')}>
            <input className="input" type="email" value={f.email} maxLength={200} onChange={(e) => up('email')(e.target.value)} />
          </Field>
        </div>
        <datalist id="out-industries">
          {industries.map((x) => (
            <option key={x} value={x} />
          ))}
        </datalist>
        <Field label={t('out.tags')} hint={t('out.tags_hint')}>
          <div className="out-tag-input">
            {tags.map((x) => (
              <span key={x} className="tag">
                {x}
                <button type="button" className="chip-x" aria-label={t('out.remove_tag', { tag: x })} onClick={() => setTags(tags.filter((y) => y !== x))}>
                  <Icon name="x" size={14} />
                </button>
              </span>
            ))}
            <input
              className="input"
              list="out-tag-list"
              value={tagText}
              maxLength={40}
              aria-label={t('out.add_tag')}
              placeholder={tags.length < 12 ? t('out.add_tag') : ''}
              disabled={tags.length >= 12}
              onChange={(e) => setTagText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  addTag();
                }
              }}
              onBlur={addTag}
            />
            <datalist id="out-tag-list">
              {(setup?.tags ?? []).map((x) => (
                <option key={x} value={x} />
              ))}
            </datalist>
          </div>
        </Field>
        <Field label={t('out.note')} counter={`${f.note.length}/2000`}>
          <textarea className="input" rows={3} maxLength={2000} value={f.note} onChange={(e) => up('note')(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

// ---- one company -------------------------------------------------------------------------------

function CompanyDialog({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => Promise<void> }) {
  const { can } = useSession();
  const manage = can('outreach.manage');
  const { data: c, reload } = useAction<Company>('outreach.company', { id });
  const [editing, setEditing] = useState(false);
  const [person, setPerson] = useState<ContactFull | 'new' | null>(null);
  const [note, setNote] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [writeTo, setWriteTo] = useState<string[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [enrollIds, setEnrollIds] = useState<string[] | null>(null);
  const [recording, setRecording] = useState(false);
  const [arranging, setArranging] = useState(false);
  const send = can('outreach.send');
  const { data: seqs, reload: reloadSeqs } = useAction<CompanySequence[]>('outreach.company_sequences', { company: id });
  const { busy, run } = useSave();
  const changed = async () => {
    await reload();
    await reloadSeqs();
    await onChanged();
  };
  if (!c) {
    return (
      <Dialog title={t('common.loading')} onClose={onClose} size="lg">
        <Skeleton h={360} />
      </Dialog>
    );
  }
  if (editing)
    return (
      <CompanyForm
        company={c}
        onClose={() => setEditing(false)}
        onSaved={async () => {
          setEditing(false);
          await changed();
        }}
      />
    );
  const people = c.contacts.filter((p) => p.active || showArchived);
  const archivedCount = c.contacts.filter((p) => !p.active).length;
  const setStage = (stage: Stage) =>
    void run(async () => {
      await action('outreach.set_stage', { company: c.id, stage, note: null });
      await changed();
    }, t('out.stage_saved', { stage: t(`out.st_${stage}` as MessageKey) }));
  const facts: { icon: IconName; label: string; value: ReactNode }[] = [
    { icon: 'layers', label: t('out.industry'), value: c.industry },
    { icon: 'pin', label: t('out.place'), value: place(c) || null },
    { icon: 'truck', label: t('out.fleet'), value: c.fleet_size === null ? null : tn('out.vehicles', c.fleet_size) },
    { icon: 'users', label: t('out.employees'), value: c.employees === null ? null : String(c.employees) },
    {
      icon: 'globe',
      label: t('out.website'),
      value: c.website ? (
        <a href={/^https?:\/\//.test(c.website) ? c.website : `https://${c.website}`} target="_blank" rel="noopener noreferrer" className="link">
          {c.domain ?? c.website}
        </a>
      ) : null,
    },
    { icon: 'phone', label: t('out.phone'), value: c.phone ? <a href={`tel:${c.phone}`}>{c.phone}</a> : null },
    { icon: 'mail', label: t('out.company_email'), value: c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : null },
    { icon: 'contact', label: t('out.owner'), value: c.owner_name },
  ];
  return (
    <Dialog
      title={c.name}
      onClose={onClose}
      size="lg"
      footer={
        manage ? (
          <>
            <span className="foot-actions">
              <Button
                variant="ghost"
                icon={c.active ? 'archive' : 'undo'}
                busy={busy}
                onClick={() => {
                  if (c.active && !window.confirm(t('out.archive_confirm', { name: c.name }))) return;
                  void run(
                    async () => {
                      await action('outreach.archive_company', { id: c.id, active: !c.active });
                      await changed();
                    },
                    c.active ? t('out.archived') : t('out.restored'),
                  );
                }}
              >
                {c.active ? t('out.archive') : t('out.restore')}
              </Button>
            </span>
            {c.active ? (
              <Button variant="primary" icon="edit" onClick={() => setEditing(true)}>
                {t('out.edit')}
              </Button>
            ) : null}
          </>
        ) : undefined
      }
    >
      <div className="form-stack">
        <div className="out-head">
          <StageBadge stage={c.stage} />
          {!c.active ? <StatusBadge tone="neutral" icon="archive" label={t('out.is_archived')} /> : null}
          {c.tags.map((x) => (
            <span key={x} className="tag">
              {x}
            </span>
          ))}
          {manage && c.active ? (
            <label className="out-stage-pick">
              <span className="muted small">{t('out.move_to')}</span>
              <select className="input" value={c.stage} disabled={busy} onChange={(e) => setStage(e.target.value as Stage)}>
                {STAGES.map((s) => (
                  <option key={s} value={s}>
                    {t(`out.st_${s}` as MessageKey)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
        <dl className="out-facts">
          {facts
            .filter((x) => x.value)
            .map((x) => (
              <div key={x.label}>
                <dt>
                  <Icon name={x.icon} size={14} /> {x.label}
                </dt>
                <dd>{x.value}</dd>
              </div>
            ))}
        </dl>
        {c.note ? <p className="out-note">{c.note}</p> : null}
        <section className="panel" aria-labelledby="out-people">
          <div className="panel-head">
            <h2 id="out-people">{t('out.people')}</h2>
            <span className="row-actions">
              {archivedCount ? <Toggle label={tn('out.show_archived_people', archivedCount)} checked={showArchived} onChange={setShowArchived} /> : null}
              {send && c.active && c.contacts.some((p) => !p.blocked) ? (
                <>
                  <Button size="sm" variant="secondary" icon="repeat" onClick={() => setEnrollIds(c.contacts.filter((p) => !p.blocked).map((p) => p.id))}>
                    {t('sq.add_to')}
                  </Button>
                  <Button size="sm" variant="secondary" icon="send" onClick={() => setWriteTo(c.contacts.filter((p) => !p.blocked).map((p) => p.id))}>
                    {t('om.write_company')}
                  </Button>
                </>
              ) : null}
              {manage && c.active ? (
                <Button size="sm" icon="plus" onClick={() => setPerson('new')}>
                  {t('out.add_person')}
                </Button>
              ) : null}
            </span>
          </div>
          {!people.length ? (
            <EmptyState icon="users" title={t('out.no_people')} body={t('out.no_people_hint')} />
          ) : (
            <ul className="out-people">
              {people.map((p) => (
                <li key={p.id} className={p.active ? '' : 'muted'}>
                  <div className="out-person">
                    <span className="avatar sm" aria-hidden="true">
                      {(p.first_name[0] ?? '') + (p.last_name?.[0] ?? '')}
                    </span>
                    <span className="vat-party">
                      <span className="strong">
                        {personName(p)}
                        {p.is_primary ? <span className="muted small"> · {t('out.primary')}</span> : null}
                      </span>
                      <span className="muted small">{[p.job_title, p.email ?? t('out.no_email')].filter(Boolean).join(' · ')}</span>
                      {p.basis ? (
                        <span className="muted small">
                          <Icon name="shieldCheck" size={12} /> {t(`out.basis_${p.basis}` as MessageKey)}
                          {p.basis_at ? ` · ${t('out.recorded_by', { who: p.basis_by_name ?? '—', when: since(p.basis_at) })}` : ''}
                        </span>
                      ) : null}
                      {(seqs ?? [])
                        .filter((e) => e.contact === p.id && e.state === 'active')
                        .map((e) => (
                          <span key={e.id} className="muted small">
                            <Icon name="repeat" size={12} /> {e.sequence_name} · {enrollmentText(e)}
                          </span>
                        ))}
                    </span>
                  </div>
                  <span className="row-actions">
                    <ReachBadge blocked={p.blocked} />
                    {send && !p.blocked ? <IconButton icon="send" label={t('om.write_to', { name: personName(p) })} onClick={() => setWriteTo([p.id])} /> : null}
                    {manage && c.active ? <IconButton icon="edit" label={t('out.edit_person', { name: personName(p) })} onClick={() => setPerson(p)} /> : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="panel" aria-labelledby="out-timeline">
          <div className="panel-head">
            <h2 id="out-timeline">{t('out.timeline')}</h2>
            {manage && c.active && c.contacts.some((p) => p.active) ? (
              <span className="row-actions">
                <Button size="sm" variant="secondary" icon="reply" onClick={() => setRecording(true)}>
                  {t('orp.record')}
                </Button>
                <Button size="sm" variant="secondary" icon="calendar" onClick={() => setArranging(true)}>
                  {t('odm.arrange')}
                </Button>
              </span>
            ) : null}
          </div>
          {manage ? (
            <div className="out-note-add">
              <textarea className="input" rows={2} maxLength={2000} value={note} placeholder={t('out.note_placeholder')} aria-label={t('out.add_note')} onChange={(e) => setNote(e.target.value)} />
              <Button
                size="sm"
                icon="note"
                busy={busy}
                disabled={!note.trim()}
                onClick={() =>
                  void run(async () => {
                    await action('outreach.add_note', { company: c.id, text: note.trim() });
                    setNote('');
                    await changed();
                  }, t('out.note_added'))
                }
              >
                {t('out.add_note')}
              </Button>
            </div>
          ) : null}
          <ol className="out-timeline">
            {c.events.map((e) => (
              <li key={e.id}>
                <span className="out-dot" aria-hidden="true">
                  <Icon name={eventIcon(e)} size={14} />
                </span>
                <div>
                  <span>{eventText(e)}</span>
                  {e.text ? <span className="out-quote">{e.text}</span> : null}
                  <span className="muted small">
                    {dateTime(e.at)}
                    {e.actor_name ? ` · ${e.actor_name}` : ''}
                    {typeof e.data?.message === 'string' ? (
                      <>
                        {' · '}
                        <button type="button" className="link" onClick={() => setMessage(String(e.data.message))}>
                          {t('om.open_email')}
                        </button>
                      </>
                    ) : null}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </section>
      </div>
      {writeTo ? (
        <ComposeDialog
          contacts={writeTo}
          onClose={() => setWriteTo(null)}
          onSent={changed}
        />
      ) : null}
      {message ? <MessageDialog id={message} onClose={() => setMessage(null)} onChanged={changed} /> : null}
      {enrollIds ? <EnrollDialog contacts={enrollIds} onClose={() => setEnrollIds(null)} onDone={changed} /> : null}
      {recording ? <RecordReplyDialog people={c.contacts.filter((p) => p.active).map((p) => ({ id: p.id, name: personName(p) }))} onClose={() => setRecording(false)} onDone={changed} /> : null}
      {arranging ? (
        <DemoForm
          company={c.id}
          contact={null}
          demo={null}
          people={c.contacts.filter((p) => p.active).map((p) => ({ id: p.id, name: personName(p) }))}
          onClose={() => setArranging(false)}
          onSaved={async () => (setArranging(false), await changed())}
        />
      ) : null}
      {person ? (
        <ContactForm
          company={c}
          person={person === 'new' ? null : person}
          onClose={() => setPerson(null)}
          onSaved={async () => {
            setPerson(null);
            await changed();
          }}
        />
      ) : null}
    </Dialog>
  );
}

function eventIcon(e: TimelineEvent): IconName {
  switch (e.kind) {
    case 'created':
      return 'plus';
    case 'imported':
      return 'upload';
    case 'stage':
      return 'layers';
    case 'contact':
      return 'contact';
    case 'basis':
      return 'shieldCheck';
    case 'owner':
      return 'users';
    case 'email':
      return 'send';
    case 'opened':
      return 'eye';
    case 'clicked':
      return 'link';
    case 'replied':
      return 'reply';
    case 'bounced':
      return 'alert';
    case 'unsubscribed':
      return 'bellOff';
    case 'demo':
      return 'calendar';
    case 'sequence':
      return 'repeat';
    default:
      return 'note';
  }
}

function eventText(e: TimelineEvent): string {
  const who = e.contact_name || t('out.someone');
  const d = e.data ?? {};
  switch (e.kind) {
    case 'created':
      return t('out.ev_created');
    case 'imported':
      return t('out.ev_imported');
    case 'stage':
      return t(d.auto ? 'out.ev_stage_auto' : 'out.ev_stage', {
        from: t(`out.st_${String(d.from)}` as MessageKey),
        to: t(`out.st_${String(d.to)}` as MessageKey),
      });
    case 'contact':
      return d.added ? t('out.ev_person_added', { who }) : d.archived ? t('out.ev_person_archived', { who }) : t('out.ev_person_back', { who });
    case 'basis':
      return d.to ? t('out.ev_basis', { who, basis: t(`out.basis_${String(d.to)}` as MessageKey) }) : t('out.ev_basis_removed', { who });
    case 'owner':
      return t('out.ev_owner');
    case 'email':
      return t('out.ev_email', { who });
    case 'opened':
      return t('out.ev_opened', { who });
    case 'clicked':
      return t('out.ev_clicked', { who });
    case 'replied':
      return d.intent ? t('orp.ev_replied', { who, intent: t(`orp.in_${String(d.intent)}` as MessageKey) }) : t('out.ev_replied', { who });
    case 'bounced':
      return t('out.ev_bounced', { who });
    case 'unsubscribed':
      return t('out.ev_unsubscribed', { who });
    case 'demo':
      return t('out.ev_demo', { who });
    case 'sequence':
      return d.started
        ? t('sq.ev_started', { who })
        : d.finished
          ? t('sq.ev_finished', { who })
          : t('sq.ev_stopped', { who, why: t(`sq.why_${String(d.stopped ?? 'manual')}` as MessageKey) });
    default:
      return d.archived === true ? t('out.ev_archived') : d.archived === false ? t('out.ev_restored') : t('out.ev_note');
  }
}

// ---- a person at the company -------------------------------------------------------------------

function ContactForm({ company, person, onClose, onSaved }: { company: Company; person: ContactFull | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [f, setF] = useState({
    first_name: person?.first_name ?? '',
    last_name: person?.last_name ?? '',
    job_title: person?.job_title ?? '',
    email: person?.email ?? '',
    phone: person?.phone ?? '',
    linkedin: person?.linkedin ?? '',
    basis_note: person?.basis_note ?? '',
  });
  const [basis, setBasis] = useState<Basis | null>(person?.basis ?? null);
  const [primary, setPrimary] = useState(person?.is_primary ?? !company.contacts.some((p) => p.active));
  const [tried, setTried] = useState(false);
  const up = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }));
  const errors = {
    first_name: !f.first_name.trim() ? t('out.e_first_name') : null,
    email: f.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim()) ? t('out.e_email') : basis && !f.email.trim() ? t('out.e_email_for_basis') : null,
    linkedin: f.linkedin.trim() && !/^https:\/\/([a-z]{2,3}\.)?linkedin\.com\//i.test(f.linkedin.trim()) ? t('out.e_linkedin') : null,
  };
  const save = () => {
    setTried(true);
    if (errors.first_name || errors.email || errors.linkedin) return;
    void run(
      async () => {
        await action('outreach.save_contact', {
          id: person?.id ?? null,
          company: company.id,
          first_name: f.first_name.trim(),
          last_name: f.last_name.trim() || null,
          job_title: f.job_title.trim() || null,
          email: f.email.trim() || null,
          phone: f.phone.trim() || null,
          linkedin: f.linkedin.trim() || null,
          is_primary: primary,
          basis,
          basis_note: basis ? f.basis_note.trim() || null : null,
        });
        await onSaved();
      },
      person ? t('out.person_saved') : t('out.person_added'),
    );
  };
  const eu = company.country ? EU.has(company.country) : false;
  return (
    <Dialog
      title={person ? t('out.edit_person', { name: personName(person) }) : t('out.add_person_at', { name: company.name })}
      onClose={onClose}
      size="md"
      footer={
        <>
          {person ? (
            <span className="foot-actions">
              {person.active && person.email && person.email_state !== 'bounced' ? (
                <Button
                  variant="ghost"
                  icon="alert"
                  busy={busy}
                  onClick={() => {
                    if (!window.confirm(t('om.bounce_confirm', { email: person.email ?? '' }))) return;
                    void run(async () => {
                      await action('outreach.mark_bounced', { contact: person.id, reason: null });
                      await onSaved();
                    }, t('om.bounce_marked'));
                  }}
                >
                  {t('om.mark_bounced')}
                </Button>
              ) : null}
              <Button
                variant="ghost"
                icon={person.active ? 'archive' : 'undo'}
                busy={busy}
                onClick={() =>
                  void run(
                    async () => {
                      await action('outreach.archive_contact', { id: person.id, active: !person.active });
                      await onSaved();
                    },
                    person.active ? t('out.person_archived') : t('out.person_back'),
                  )
                }
              >
                {person.active ? t('out.archive_person') : t('out.restore_person')}
              </Button>
            </span>
          ) : null}
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {person ? t('common.save') : t('out.add')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        {person?.email_state === 'bounced' ? (
          <p className="callout callout-danger">
            <Icon name="alert" size={16} />
            <span>{t('out.bounced_note', { when: person.bounced_at ? dateTime(person.bounced_at) : '—', reason: person.bounce_reason ?? '—' })}</span>
          </p>
        ) : null}
        {person?.opted_out_at ? (
          <p className="callout callout-warn">
            <Icon name="bellOff" size={16} />
            <span>{t('out.opted_out_note', { when: dateTime(person.opted_out_at) })}</span>
          </p>
        ) : null}
        <div className="form-grid">
          <Field label={t('out.first_name')} required error={tried ? errors.first_name : null}>
            <input className="input" value={f.first_name} maxLength={80} autoComplete="off" onChange={(e) => up('first_name')(e.target.value)} data-autofocus />
          </Field>
          <Field label={t('out.last_name')}>
            <input className="input" value={f.last_name} maxLength={80} autoComplete="off" onChange={(e) => up('last_name')(e.target.value)} />
          </Field>
          <Field label={t('out.job_title')} hint={t('out.job_title_hint')}>
            <input className="input" value={f.job_title} maxLength={100} onChange={(e) => up('job_title')(e.target.value)} />
          </Field>
          <Field label={t('out.email')} error={tried ? errors.email : null}>
            <input className="input" type="email" value={f.email} maxLength={200} autoComplete="off" onChange={(e) => up('email')(e.target.value)} />
          </Field>
          <Field label={t('out.phone')}>
            <input className="input" type="tel" value={f.phone} maxLength={40} onChange={(e) => up('phone')(e.target.value)} />
          </Field>
          <Field label={t('out.linkedin')} error={tried ? errors.linkedin : null}>
            <input className="input" value={f.linkedin} maxLength={200} inputMode="url" placeholder="https://www.linkedin.com/in/…" onChange={(e) => up('linkedin')(e.target.value)} />
          </Field>
        </div>
        <Toggle label={t('out.primary_label')} checked={primary} onChange={setPrimary} />
        <fieldset className="out-basis">
          <legend>{t('out.basis_title')}</legend>
          <p className="muted small">{t('out.basis_intro')}</p>
          <div className="out-basis-options" role="radiogroup" aria-label={t('out.basis_title')}>
            {BASES.map((b) => (
              <button key={b} type="button" role="radio" aria-checked={basis === b} className={`out-basis-option${basis === b ? ' selected' : ''}`} onClick={() => setBasis(basis === b ? null : b)}>
                <span className="strong">
                  <Icon name={basis === b ? 'checkCircle' : 'circle'} size={16} /> {t(`out.basis_${b}` as MessageKey)}
                </span>
                <span className="muted small">{t(`out.basis_${b}_hint` as MessageKey)}</span>
              </button>
            ))}
          </div>
          {basis === 'legitimate_interest' && eu ? (
            <p className="callout callout-info">
              <Icon name="info" size={16} />
              <span>{t('out.eu_note', { country: countryName(company.country) ?? '' })}</span>
            </p>
          ) : null}
          {!basis ? (
            <p className="muted small">
              <Icon name="info" size={14} /> {t('out.no_basis_note')}
            </p>
          ) : (
            <Field label={t('out.basis_note')} hint={t('out.basis_note_hint')} counter={`${f.basis_note.length}/300`}>
              <input className="input" value={f.basis_note} maxLength={300} onChange={(e) => up('basis_note')(e.target.value)} />
            </Field>
          )}
        </fieldset>
      </div>
    </Dialog>
  );
}

// ---- everyone ----------------------------------------------------------------------------------

const CONTACT_FILTERS = ['all', 'reachable', 'no_basis', 'bounced', 'unsubscribed'] as const;

function ContactsTab({ onOpen }: { onOpen: (company: string) => void }) {
  const [filter, setFilter] = useState<(typeof CONTACT_FILTERS)[number]>('all');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const id = setTimeout(() => (setQ(search.trim()), setOffset(0)), 300);
    return () => clearTimeout(id);
  }, [search]);
  const { data } = useAction<{ total: number; rows: ContactRow[] }>('outreach.contacts', { filter, offset, ...(q ? { search: q } : {}) });
  return (
    <section className="panel">
      <div className="toolbar">
        <div className="segmented subtle out-seg" role="radiogroup" aria-label={t('out.show')}>
          {CONTACT_FILTERS.map((f) => (
            <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => (setFilter(f), setOffset(0))}>
              {t(`out.cf_${f}` as MessageKey)}
            </button>
          ))}
        </div>
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={search} placeholder={t('out.search_people')} aria-label={t('out.search_people')} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </div>
      {!data ? (
        <Skeleton h={240} />
      ) : (
        <>
          <TableWrap label={t('out.tab_contacts')}>
            <Table
              view="outreach.contacts"
              head={[t('out.c_person'), t('out.c_company'), t('out.c_basis'), t('out.c_can_write')]}
              phoneHide={[1, 2]}
              left={[1, 2]}
              empty={t('out.nobody_here')}
              rows={data.rows.map((p) => ({
                key: p.id,
                onClick: () => onOpen(p.company),
                cells: [
                  <span key="p" className="vat-party">
                    <span className="strong">{personName(p)}</span>
                    <span className="muted small">{[p.job_title, p.email ?? t('out.no_email')].filter(Boolean).join(' · ')}</span>
                  </span>,
                  <span key="c" className="vat-party">
                    <span>{p.company_name}</span>
                    <span className="muted small">{t(`out.st_${p.company_stage}` as MessageKey)}</span>
                  </span>,
                  p.basis ? t(`out.basis_${p.basis}` as MessageKey) : '—',
                  <ReachBadge key="r" blocked={p.blocked} />,
                ],
              }))}
            />
          </TableWrap>
          <div className="row-actions mkt-pager">
            <span className="muted small">{t('out.showing', { from: data.total ? offset + 1 : 0, to: offset + data.rows.length, total: data.total })}</span>
            <IconButton icon="back" label={t('out.prev')} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 100))} />
            <IconButton icon="chevronRight" label={t('out.next')} disabled={offset + data.rows.length >= data.total} onClick={() => setOffset(offset + 100)} />
          </div>
        </>
      )}
    </section>
  );
}
